import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';
import { LuArchiveX, LuLoader, LuPlus } from 'react-icons/lu';

import { AccountOnboardingOwnerView, AccountOnboardingView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { getAccountOwnerName, titleCase } from '@/utils';

import { DashboardCard } from '../DashboardCard';
import { useDismissAccountOnboarding, useListNewAccounts } from './account-onboarding-queries';
import { AccountOnboardingSheet } from './AccountOnboardingSheet';

const columnHelper = createColumnHelper<AccountOnboardingView>();

export const AccountOnboarding = () => {
    const { data: accounts = [], isLoading } = useListNewAccounts();
    const dismissMutation = useDismissAccountOnboarding();
    const [selectedAccount, setSelectedAccount] = useState<AccountOnboardingView | null>(null);
    const [accountToDismiss, setAccountToDismiss] = useState<AccountOnboardingView | null>(null);

    const handleDismiss = () => {
        if (!accountToDismiss?.tenantGUID) return;

        dismissMutation.mutate(
            {
                accountNumber: accountToDismiss.accountNumber,
                tenantGUID: accountToDismiss.tenantGUID,
            },
            { onSuccess: () => setAccountToDismiss(null) },
        );
    };

    const columns = [
        columnHelper.accessor('accountNumber', {
            id: 'accountNumber',
            header: 'Account#',
            cell: (info) => (
                <div>
                    <span className="text-cocoa-1000 font-base text-base font-thin">{info.getValue()}</span>
                    <p className="text-cocoa-500 text-sm font-thin">{titleCase(info.row.original.custodyType)}</p>
                </div>
            ),
        }),
        columnHelper.accessor('accountType', {
            id: 'accountType',
            header: 'Account Type',
            cell: (info) => (
                <span className="text-cocoa-1000 text-base font-thin">
                    {info.row.original.inherited ? 'Inherited ' : ''}
                    {titleCase(info.getValue())}
                </span>
            ),
        }),
        columnHelper.accessor((row) => row.accountOwners, {
            id: 'accountOwner',
            header: 'Account Owner',
            cell: (info) => <AccountOwnersCell accountOwners={info.getValue()} />,
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: (info) => (
                <div className="flex justify-end gap-2 pr-2">
                    <Button
                        variant="outline2"
                        size="sm"
                        onClick={() => setSelectedAccount(info.row.original)}
                        className="gap-1.5 px-4 text-base"
                    >
                        <LuPlus className="h-4 w-4" />
                        Create Account
                    </Button>
                    <Button
                        variant="ghost2"
                        size="sm"
                        onClick={() => setAccountToDismiss(info.row.original)}
                        className="gap-1.5 px-3 text-base hover:text-red-600"
                        aria-label={`Dismiss account ${info.row.original.accountNumber}`}
                    >
                        <LuArchiveX className="h-4 w-4" />
                        Dismiss
                    </Button>
                </div>
            ),
        }),
    ];

    const table = useReactTable({
        data: accounts,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (isLoading || accounts.length === 0) return null;

    return (
        <DashboardCard title="New Accounts" size="full" className="flex flex-col border-0 p-0 py-6">
            <div className="relative">
                <div className="max-h-[320px] overflow-auto">
                    <table className="w-full text-sm">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className="text-muted-foreground px-1.5 pb-2 text-left text-xs font-thin"
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className="border-border hover:bg-cocoa-0/50 border-t transition-colors last:border-b"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-1.5 py-4">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-4 bg-gradient-to-t from-white to-transparent"></div>
            </div>
            {selectedAccount && (
                <AccountOnboardingSheet
                    accountOnboardingView={selectedAccount}
                    open={!!selectedAccount}
                    onOpenChange={(open) => !open && setSelectedAccount(null)}
                />
            )}
            {accountToDismiss && (
                <Dialog open onOpenChange={(open) => !open && !dismissMutation.isPending && setAccountToDismiss(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Dismiss New Account</DialogTitle>
                            <DialogDescription>
                                Dismiss account{' '}
                                <span className="font-mono font-medium">{accountToDismiss.accountNumber}</span>? It will
                                no longer appear in account onboarding.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setAccountToDismiss(null)}
                                disabled={dismissMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleDismiss} disabled={dismissMutation.isPending}>
                                {dismissMutation.isPending ? <LuLoader className="h-4 w-4 animate-spin" /> : 'Dismiss'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </DashboardCard>
    );
};

const AccountOwnersCell = (props: { accountOwners: AccountOnboardingOwnerView[] }) => {
    if (props.accountOwners.length === 0) return <span className="text-cocoa-1000 text-base font-thin">—</span>;

    return (
        <div className="flex flex-col gap-0.5">
            {props.accountOwners.map((owner, i) => (
                <span key={i} className="text-cocoa-1000 text-base font-thin">
                    {getAccountOwnerName(owner) || '—'}
                </span>
            ))}
        </div>
    );
};
